import { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import {
  getUserRoleAsync,
  isAdminAsync,
  isModeratorAsync,
  isSeniorModeratorAsync,
  type UserRole,
} from "../utility/adminUtils";

interface UseAdminRoleReturn {
  role: UserRole;
  isAdmin: boolean;
  isModerator: boolean;
  isSeniorModerator: boolean;
  loading: boolean;
}

/**
 * Hook to check if current user is admin/moderator
 * Automatically refreshes token to get latest custom claims
 * 
 * @param user - Firebase User object from useAppContext
 * @returns Object with role information and loading state
 */
export const useAdminRole = (user: User | null): UseAdminRoleReturn => {
  const [role, setRole] = useState<UserRole>(null);
  const [isAdminValue, setIsAdminValue] = useState<boolean>(false);
  const [isModeratorValue, setIsModeratorValue] = useState<boolean>(false);
  const [isSeniorModeratorValue, setIsSeniorModeratorValue] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setIsAdminValue(false);
      setIsModeratorValue(false);
      setIsSeniorModeratorValue(false);
      setLoading(false);
      return;
    }

    // Refresh token to get latest custom claims
    const refreshAndGetRole = async () => {
      try {
        setLoading(true);
        // Force token refresh to get latest custom claims
        const userRole = await getUserRoleAsync(user, true);
        
        // Calculate all role checks from the role value (more efficient)
        setRole(userRole);
        setIsAdminValue(userRole === "admin");
        setIsModeratorValue(
          userRole === "admin" || userRole === "senior_moderator" || userRole === "moderator"
        );
        setIsSeniorModeratorValue(userRole === "admin" || userRole === "senior_moderator");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error refreshing user token:", error);
        // Fallback: try without refresh
        try {
          const userRole = await getUserRoleAsync(user, false);
          
          setRole(userRole);
          setIsAdminValue(userRole === "admin");
          setIsModeratorValue(
            userRole === "admin" || userRole === "senior_moderator" || userRole === "moderator"
          );
          setIsSeniorModeratorValue(userRole === "admin" || userRole === "senior_moderator");
        } catch (fallbackError) {
          // eslint-disable-next-line no-console
          console.error("Error getting user role (fallback):", fallbackError);
          setRole(null);
          setIsAdminValue(false);
          setIsModeratorValue(false);
          setIsSeniorModeratorValue(false);
        }
      } finally {
        setLoading(false);
      }
    };

    refreshAndGetRole();
  }, [user]);

  return {
    role,
    isAdmin: isAdminValue,
    isModerator: isModeratorValue,
    isSeniorModerator: isSeniorModeratorValue,
    loading,
  };
};

